import {
  AdminOrder,
  AdminOrderItem,
  AdminProductRecord,
  AdminUserRecord,
  LowStockItem,
  PaymentQueueItem,
  TopMetric
} from '@core/models/admin.model';
import { ApiRole, ApiUser } from '@core/models/api/user.model';
import { ApiProduct, ApiProductStatus, ProductFormValues } from '@core/models/api/product.model';
import { ApiOrder, ApiOrderItem, ApiOrderStatus } from '@core/models/api/order.model';
import { ApiPayment, ApiPaymentMethod, ApiPaymentStatus } from '@core/models/api/payment.model';
import { ApiDashboardSummary } from '@core/models/api/dashboard.model';

export type FrontendRole = 'Admin' | 'Manager' | 'Customer';
export type FrontendProductStatus = 'Available' | 'Limited' | 'Out of Stock';
export type FrontendPaymentStatus = 'PENDING' | 'VERIFIED';
export type FrontendPaymentMethod = 'ONLINE' | 'OFFLINE';

const ROLE_BADGE_CLASSES: Record<FrontendRole, string> = {
  Customer: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Manager: 'bg-sky-100 text-sky-900 border-sky-300',
  Admin: 'bg-red-100 text-red-900 border-red-300'
};

const PRODUCT_STATUS_BADGE_CLASSES: Record<FrontendProductStatus, string> = {
  Available: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Limited: 'bg-sky-100 text-sky-800 border-sky-300',
  'Out of Stock': 'bg-red-100 text-red-900 border-red-300'
};

const API_TO_FRONTEND_ROLE: Record<ApiRole, FrontendRole> = {
  admin: 'Admin',
  manager: 'Manager',
  customer: 'Customer'
};

const FRONTEND_TO_API_ROLE: Record<FrontendRole, ApiRole> = {
  Admin: 'admin',
  Manager: 'manager',
  Customer: 'customer'
};

const API_TO_FRONTEND_PRODUCT_STATUS: Record<ApiProductStatus, FrontendProductStatus> = {
  available: 'Available',
  limited: 'Limited',
  out_of_stock: 'Out of Stock'
};

const FRONTEND_TO_API_PRODUCT_STATUS: Record<FrontendProductStatus, ApiProductStatus> = {
  Available: 'available',
  Limited: 'limited',
  'Out of Stock': 'out_of_stock'
};

const API_TO_FRONTEND_PAYMENT_STATUS: Record<ApiPaymentStatus, FrontendPaymentStatus> = {
  pending: 'PENDING',
  verified: 'VERIFIED'
};

const API_TO_FRONTEND_PAYMENT_METHOD: Record<ApiPaymentMethod, FrontendPaymentMethod> = {
  online: 'ONLINE',
  offline: 'OFFLINE'
};

const FRONTEND_TO_API_PAYMENT_METHOD: Record<FrontendPaymentMethod, ApiPaymentMethod> = {
  ONLINE: 'online',
  OFFLINE: 'offline'
};

export function toFrontendRole(role: ApiRole): FrontendRole {
  return API_TO_FRONTEND_ROLE[role] ?? 'Customer';
}

export function toApiRole(role: string): ApiRole {
  return FRONTEND_TO_API_ROLE[role as FrontendRole] ?? 'customer';
}

export function roleBadgeClass(role: string): string {
  return ROLE_BADGE_CLASSES[role as FrontendRole] ?? 'bg-slate-100 text-slate-800 border-slate-300';
}

export function toFrontendProductStatus(status: ApiProductStatus): FrontendProductStatus {
  return API_TO_FRONTEND_PRODUCT_STATUS[status] ?? 'Available';
}

export function toApiProductStatus(status: string): ApiProductStatus {
  return FRONTEND_TO_API_PRODUCT_STATUS[status as FrontendProductStatus] ?? 'available';
}

export function productStatusBadgeClass(status: string): string {
  return PRODUCT_STATUS_BADGE_CLASSES[status as FrontendProductStatus] ?? 'bg-slate-100 text-slate-800 border-slate-300';
}

/** e.g. "in_transit" -> "IN-TRANSIT" */
export function toFrontendOrderStatus(status: string): string {
  return status.toUpperCase().replace(/_/g, '-');
}

/** e.g. "IN-TRANSIT" -> "in_transit" */
export function toApiOrderStatus(status: string): ApiOrderStatus {
  return status.toLowerCase().replace(/-/g, '_') as ApiOrderStatus;
}

export function formatDisplayDate(isoDate?: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function toAdminUserRecord(apiUser: ApiUser): AdminUserRecord {
  const role = toFrontendRole(apiUser.role);
  return {
    id: apiUser.code,
    apiId: apiUser.id,
    name: apiUser.name,
    email: apiUser.email,
    role,
    roleBadgeClass: roleBadgeClass(role),
    mobileNumber: apiUser.mobileNumber,
    companyName: apiUser.companyName,
    gstin: apiUser.gstin,
    creditLimit: apiUser.creditLimit !== undefined && apiUser.creditLimit !== null ? Number(apiUser.creditLimit) : undefined,
    creditUsed: apiUser.creditUsed !== undefined && apiUser.creditUsed !== null ? Number(apiUser.creditUsed) : undefined,
    paymentTerms: apiUser.paymentTerms,
    address: apiUser.address,
    createdAt: formatDisplayDate(apiUser.createdAt)
  };
}

export function toCreateUserRequest(user: Partial<AdminUserRecord>) {
  return {
    name: user.name || '',
    email: user.email || '',
    password: user.password || '',
    role: toApiRole(user.role || 'Customer'),
    mobileNumber: user.mobileNumber || undefined,
    companyName: user.companyName || undefined,
    gstin: user.gstin || undefined,
    creditLimit: user.creditLimit ?? undefined,
    creditUsed: user.creditUsed ?? undefined,
    paymentTerms: user.paymentTerms || undefined,
    address: user.address || undefined
  };
}

export function toUpdateUserRequest(user: Partial<AdminUserRecord>) {
  return {
    name: user.name || undefined,
    email: user.email || undefined,
    password: user.password || undefined,
    role: user.role ? toApiRole(user.role) : undefined,
    mobileNumber: user.mobileNumber || undefined,
    companyName: user.companyName || undefined,
    gstin: user.gstin || undefined,
    creditLimit: user.creditLimit ?? undefined,
    creditUsed: user.creditUsed ?? undefined,
    paymentTerms: user.paymentTerms || undefined,
    address: user.address || undefined
  };
}

import { environment } from '../../../environments/environment';

export function resolveImageUrl(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('http://localhost:3000/uploads/')) {
    const backendRoot = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    return trimmed.replace('http://localhost:3000', backendRoot);
  }
  if (trimmed.startsWith('http://localhost:3000/')) {
    const backendRoot = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    return trimmed.replace('http://localhost:3000', backendRoot);
  }
  if (trimmed.startsWith('/uploads/')) {
    const backendRoot = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    return `${backendRoot}${trimmed}`;
  }
  return trimmed;
}

export function toAdminProductRecord(apiProduct: ApiProduct): AdminProductRecord {
  const status = toFrontendProductStatus(apiProduct.status);
  const qtyNum = apiProduct.stockQty !== undefined && apiProduct.stockQty !== null ? Number(apiProduct.stockQty) : null;
  const stock = qtyNum !== null
    ? `${Number.isNaN(qtyNum) ? 0 : qtyNum} ${apiProduct.stockUnit || 'Kg'}`
    : '0 Kg';

  const rawUrls = apiProduct.imageUrl
    ? apiProduct.imageUrl.split(',').map(u => resolveImageUrl(u)).filter(Boolean)
    : [];
  const imageUrl = rawUrls.join(',');

  return {
    sku: apiProduct.sku,
    apiId: apiProduct.id,
    name: apiProduct.name,
    subtext: apiProduct.subtext || '',
    stock,
    price: apiProduct.price,
    expiry: formatDisplayDate(apiProduct.expiry),
    status,
    statusBadgeClass: productStatusBadgeClass(status),
    category: apiProduct.category,
    qty: apiProduct.stockQty,
    unit: apiProduct.stockUnit,
    batch: apiProduct.batch,
    catchDate: apiProduct.catchDate,
    landingCenter: apiProduct.landingCenter,
    warehouse: apiProduct.warehouse,
    temperature: apiProduct.temperature,
    imageUrl
  };
}

/** Splits a combined stock string like "260 Kg" back into { qty, unit }. */
export function parseStockString(stock: string): { qty?: number; unit?: string } {
  const match = stock.trim().match(/^([\d.]+)\s*(.*)$/);
  if (!match) return {};
  const qty = Number(match[1]);
  return { qty: Number.isNaN(qty) ? undefined : qty, unit: match[2] || undefined };
}

/** Converts a view-model product (form values or an existing record) into the multipart-ready request shape the Products API expects. */
export function toProductFormValues(product: Partial<AdminProductRecord>): Partial<ProductFormValues> {
  const parsedStock = product.stock ? parseStockString(product.stock) : {};
  const qtyVal = parsedStock.qty ?? product.qty;
  const expiryIso = toIsoDateInputValue(product.expiry);
  const catchDateIso = toIsoDateInputValue(product.catchDate);

  return {
    name: product.name || undefined,
    subtext: product.subtext || undefined,
    category: product.category || undefined,
    stockQty: qtyVal !== undefined && qtyVal !== null && !Number.isNaN(qtyVal) ? Number(qtyVal) : undefined,
    stockUnit: parsedStock.unit ?? product.unit ?? undefined,
    price: product.price !== undefined && product.price !== null && !Number.isNaN(Number(product.price)) ? Number(product.price) : undefined,
    expiry: expiryIso || undefined,
    status: product.status ? toApiProductStatus(product.status) : undefined,
    batch: product.batch || undefined,
    catchDate: catchDateIso || undefined,
    landingCenter: product.landingCenter || undefined,
    warehouse: product.warehouse || undefined,
    temperature: product.temperature || undefined
  };
}

/** Converts an ISO or human-readable date string into the "YYYY-MM-DD" shape an `<input type="date">` expects. */
export function toIsoDateInputValue(value?: string): string {
  if (!value) return '';
  const str = value.trim();
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toAdminOrderItem(apiItem: ApiOrderItem): AdminOrderItem {
  return {
    name: apiItem.productName,
    qty: apiItem.qty !== undefined && apiItem.qty !== null ? Number(apiItem.qty) : 0,
    unitPrice: apiItem.unitPrice !== undefined && apiItem.unitPrice !== null ? Number(apiItem.unitPrice) : 0,
    totalPrice: apiItem.totalPrice !== undefined && apiItem.totalPrice !== null ? Number(apiItem.totalPrice) : 0
  };
}

export function toAdminOrder(apiOrder: ApiOrder): AdminOrder {
  return {
    id: apiOrder.orderNumber,
    apiId: apiOrder.id,
    buyerId: apiOrder.buyerId,
    buyerName: apiOrder.buyer?.name || 'Unknown Buyer',
    amount: apiOrder.amount !== undefined && apiOrder.amount !== null ? Number(apiOrder.amount) : 0,
    status: toFrontendOrderStatus(apiOrder.status),
    secondaryStatus: apiOrder.secondaryStatus,
    destination: apiOrder.destination,
    policy: apiOrder.policy,
    createdDate: formatDisplayDate(apiOrder.createdAt),
    isExpanded: false,
    items: (apiOrder.items || []).map(toAdminOrderItem)
  };
}

export function toFrontendPaymentStatus(status: ApiPaymentStatus): FrontendPaymentStatus {
  return API_TO_FRONTEND_PAYMENT_STATUS[status] ?? 'PENDING';
}

export function toFrontendPaymentMethod(method: ApiPaymentMethod): FrontendPaymentMethod {
  return API_TO_FRONTEND_PAYMENT_METHOD[method] ?? 'OFFLINE';
}

export function toApiPaymentMethod(method: string): ApiPaymentMethod {
  return FRONTEND_TO_API_PAYMENT_METHOD[method as FrontendPaymentMethod] ?? 'offline';
}

export function toPaymentQueueItem(apiPayment: ApiPayment): PaymentQueueItem {
  return {
    orderId: apiPayment.order?.orderNumber ?? '',
    apiId: apiPayment.id,
    customerName: apiPayment.customer?.name ?? 'Unknown Customer',
    amount: apiPayment.amount !== undefined && apiPayment.amount !== null ? Number(apiPayment.amount) : 0,
    status: toFrontendPaymentStatus(apiPayment.status),
    paymentMethod: toFrontendPaymentMethod(apiPayment.paymentMethod),
    refId: apiPayment.refId
  };
}

/** e.g. 3420000 -> "₹ 34.2L", 12500000 -> "₹ 1.25Cr", 45000 -> "₹ 45,000" */
export function formatCompactInr(amount: number): string {
  if (amount >= 10000000) {
    return `₹ ${(amount / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  }
  if (amount >= 100000) {
    return `₹ ${(amount / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  }
  return `₹ ${amount.toLocaleString('en-IN')}`;
}

/** e.g. an expiry 2 days out -> "2 Days", today -> "Today", past -> "Expired" */
export function formatDaysRemaining(isoDate?: string): string {
  if (!isoDate) return '—';
  const expiry = new Date(isoDate);
  if (Number.isNaN(expiry.getTime())) return '—';

  const msPerDay = 24 * 60 * 60 * 1000;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((expiry.getTime() - today.getTime()) / msPerDay);

  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  if (days === 1) return '1 Day';
  return `${days} Days`;
}

export function toLowStockItem(apiProduct: ApiProduct): LowStockItem {
  const qtyNum = apiProduct.stockQty !== undefined && apiProduct.stockQty !== null
    ? Number(apiProduct.stockQty)
    : null;

  return {
    product: apiProduct.name,
    warehouse: apiProduct.warehouse || '—',
    qty: qtyNum !== null && !Number.isNaN(qtyNum)
      ? `${qtyNum} ${apiProduct.stockUnit || 'Kg'}`
      : '—',
    expiry: formatDaysRemaining(apiProduct.expiry)
  };
}

export function toTopMetrics(summary: ApiDashboardSummary): TopMetric[] {
  return [
    { label: 'ORDERS TODAY', count: String(summary.ordersToday), subtext: 'New commercial demand' },
    { label: 'ACTIVE ORDERS', count: String(summary.activeOrders), subtext: 'Still moving through workflow' },
    { label: 'LOW STOCK', count: String(summary.lowStockCount), subtext: 'Items that need replenishment' },
    { label: 'OUTSTANDING', count: formatCompactInr(summary.outstandingAmount), subtext: 'Pending collection exposure' }
  ];
}
